import { useEffect, useRef } from 'react';
import type * as Api from '@ViewModels';
import { type Node, type Edge, type ReactFlowInstance } from '@xyflow/react';
import { toast } from 'sonner';
import { NODE_TYPES, type BaseNodeData } from '../types';
import type { ChainTerminus } from './useChainTermini';
import type { HistoryActionType } from './useUndoRedo';
import { findClosestTerminus, insertPastedNodesAtTerminus } from '../utils/autoLayout';
import { buildBranchCaseDeletionPlan, deleteNodes, type DeletedStepInfo } from '../utils/nodeDelete';
import { applyLayout } from '../utils/layoutEngine';
import { moveNodeAfter } from '../utils/nodeReordering';
import { DEFAULT_CANVAS_FIT_VIEW_OPTIONS } from '../utils/viewport';
import { isAutomationStepNodeData } from '../types/typeGuards';
import {
	TRIGGER_AUTOMATION_LAST_STEP_TOAST,
	TRIGGER_AUTOMATION_TRIGGER_TYPE_TOAST,
} from '../utils/triggerAutomationStepRules';

/** Stores the last known mouse position for paste operations */
interface MousePosition {
	x: number;
	y: number;
}

interface CanvasModeConfig {
	isReadOnly: boolean;
	elementsSelectable: boolean;
}

interface UndoRedoState {
	canUndo: boolean;
	canRedo: boolean;
	undo: () => void;
	redo: () => void;
}

interface CopyPasteState {
	hasClipboard: boolean;
	copySelection: (nodeIds: string[]) => void;
	pasteAtPosition: (position: { x: number; y: number }) => {
		nodes: Node<BaseNodeData>[];
		edges: Edge[];
		newNodeIds: string[];
	} | null;
}

interface GroupDragState {
	isDragging: boolean;
}

interface UseCanvasKeyboardShortcutsParams {
	nodes: Node<BaseNodeData>[];
	edges: Edge[];
	setNodes: React.Dispatch<React.SetStateAction<Node<BaseNodeData>[]>>;
	setEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
	modeConfig: CanvasModeConfig;
	undoRedo: UndoRedoState;
	copyPaste: CopyPasteState;
	takeSnapshot: (actionType: HistoryActionType, description: string) => void;
	screenToFlowPosition: ReactFlowInstance['screenToFlowPosition'];
	fitView: ReactFlowInstance['fitView'];
	zoomIn: ReactFlowInstance['zoomIn'];
	zoomOut: ReactFlowInstance['zoomOut'];
	getReactFlowNodes: () => Node<BaseNodeData>[];
	selectBranchNodes: (nodeId: string) => string[];
	groupDragState: GroupDragState;
	cancelGroupDrag: () => void;
	chainTermini: ChainTerminus[];
	/** Optional callback to open node search panel */
	openSearch?: () => void;
	/** Callback to persist step deletions to the API */
	onDeleteSteps?: (steps: DeletedStepInfo[]) => Promise<void>;
	/** Callback to persist segment-branch deletions to the API */
	onDeleteBranchCases?: (cases: { switchNodeId: string; caseId: string }[]) => Promise<void>;
	/** Callback to persist a duplication to the API */
	onDuplicateStep?: (params: {
		templateId: string;
		stepType: Api.AutomationStepType;
		step: Api.IAutomationStep;
		beforeId?: string;
	}) => Promise<void>;
	/** Callback to persist pasted steps to the API */
	onPasteSteps?: (
		terminus: ChainTerminus,
		steps: { stepType: Api.AutomationStepType; step: Api.IAutomationStep }[]
	) => Promise<void>;
	/** Step node types that are unavailable in the current editor context */
	excludedStepTypes?: string[];
}

/**
 * Hook that handles all keyboard shortcuts for the automation flow canvas.
 * Extracted from AutomationFlowCanvas to improve maintainability.
 *
 * Shortcuts handled:
 * - Ctrl/Cmd+Z: Undo
 * - Ctrl/Cmd+Shift+Z or Ctrl/Cmd+Y: Redo
 * - Ctrl/Cmd+C: Copy selection
 * - Ctrl/Cmd+V: Paste
 * - Ctrl/Cmd+A: Select all
 * - Ctrl/Cmd+B: Select branch
 * - Ctrl/Cmd+E: Expand/collapse all nodes
 * - Ctrl/Cmd+D: Duplicate selection
 * - Delete/Backspace: Delete selection
 * - Escape: Cancel group drag
 * - F or Ctrl/Cmd+0: Fit view
 * - Ctrl/Cmd++: Zoom in
 * - Ctrl/Cmd+-: Zoom out
 * - Ctrl/Cmd+F or /: Open node search panel
 * - Z: Zoom to selection
 */
export function useCanvasKeyboardShortcuts({
	nodes,
	edges,
	setNodes,
	setEdges,
	modeConfig,
	undoRedo,
	copyPaste,
	takeSnapshot,
	screenToFlowPosition,
	fitView,
	zoomIn,
	zoomOut,
	getReactFlowNodes,
	selectBranchNodes,
	groupDragState,
	cancelGroupDrag,
	chainTermini,
	openSearch,
	onDeleteSteps,
	onDeleteBranchCases,
	onDuplicateStep,
	onPasteSteps,
	excludedStepTypes,
}: UseCanvasKeyboardShortcutsParams): void {
	// Track mouse position for paste-at-cursor functionality
	const mousePositionRef = useRef<MousePosition>({ x: window.innerWidth / 2, y: window.innerHeight / 2 });

	// Track mouse movement to know where to paste
	useEffect(() => {
		const handleMouseMove = (e: MouseEvent) => {
			mousePositionRef.current = { x: e.clientX, y: e.clientY };
		};

		window.addEventListener('mousemove', handleMouseMove);
		return () => window.removeEventListener('mousemove', handleMouseMove);
	}, []);

	useEffect(() => {
		const handleKeyPress = (e: KeyboardEvent) => {
			// Ignore shortcuts when typing in inputs
			if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
				return;
			}

			const isModKey = e.ctrlKey || e.metaKey;
			const key = e.key.toLowerCase();

			// Undo - Ctrl/Cmd+Z (editing mode only)
			if (isModKey && key === 'z' && !e.shiftKey && !modeConfig.isReadOnly) {
				e.preventDefault();
				if (undoRedo.canUndo) {
					undoRedo.undo();
				}
				return;
			}

			// Redo - Ctrl/Cmd+Shift+Z or Ctrl/Cmd+Y (editing mode only)
			if (isModKey && ((key === 'z' && e.shiftKey) || key === 'y') && !modeConfig.isReadOnly) {
				e.preventDefault();
				if (undoRedo.canRedo) {
					undoRedo.redo();
				}
				return;
			}

			// Copy - Ctrl/Cmd+C (editing mode only)
			if (isModKey && e.key === 'c' && !modeConfig.isReadOnly) {
				const selectedNodeIds = nodes.filter(n => n.selected).map(n => n.id);
				if (selectedNodeIds.length > 0) {
					e.preventDefault();
					copyPaste.copySelection(selectedNodeIds);
				}
				return;
			}

			// Paste - Ctrl/Cmd+V (editing mode only)
			if (isModKey && e.key === 'v' && !modeConfig.isReadOnly) {
				if (copyPaste.hasClipboard) {
					e.preventDefault();
					const mouseFlowPosition = screenToFlowPosition(mousePositionRef.current);
					const closestTerminus = findClosestTerminus(mouseFlowPosition, chainTermini);
					// Position is irrelevant when inserting at a terminus because applyLayout recomputes it.
					const result = copyPaste.pasteAtPosition(mouseFlowPosition);
					if (result) {
						const triggerAutomationNodes = result.nodes.filter(
							node => node.type === NODE_TYPES.TRIGGER_AUTOMATION_STEP
						);
						if (triggerAutomationNodes.length > 0) {
							if (excludedStepTypes?.includes(NODE_TYPES.TRIGGER_AUTOMATION_STEP)) {
								toast.error(TRIGGER_AUTOMATION_TRIGGER_TYPE_TOAST);
								return;
							}
							// Reject if any pasted Start Automation node has a successor inside the clipboard.
							if (triggerAutomationNodes.some(node => result.edges.some(edge => edge.source === node.id))) {
								toast.error(TRIGGER_AUTOMATION_LAST_STEP_TOAST);
								return;
							}
							// Reject if inserting at a terminus that already has a downstream chain — the pasted
							// trigger step would end up mid-chain.
							if (closestTerminus) {
								const terminusHasDownstream = edges.some(edge => {
									if (edge.source !== closestTerminus.node.id) {
										return false;
									}
									const downstream = nodes.find(candidate => candidate.id === edge.target);
									return !!downstream && downstream.type !== NODE_TYPES.PHANTOM_TARGET;
								});
								if (terminusHasDownstream) {
									toast.error(TRIGGER_AUTOMATION_LAST_STEP_TOAST);
									return;
								}
							}
						}

						takeSnapshot('paste', `Paste ${result.nodes.length} node(s)`);
						if (closestTerminus) {
							// Insert into the chain at the closest terminus — mirrors drag-and-drop behavior.
							const layoutResult = insertPastedNodesAtTerminus(result, closestTerminus, nodes, edges);
							setNodes(layoutResult.nodes.map(n => ({ ...n, selected: result.newNodeIds.includes(n.id) })));
							setEdges(layoutResult.edges);

							// Persist pasted steps to the API (append at terminus).
							if (onPasteSteps) {
								const nodeIdSet = new Set(result.nodes.map(n => n.id));
								const internalEdges = result.edges.filter(
									edge => nodeIdSet.has(edge.source) && nodeIdSet.has(edge.target)
								);
								const nodeById = new Map(result.nodes.map(n => [n.id, n] as const));

								// Detect branching graphs; we only support pasting linear chains right now.
								const outCounts = new Map<string, number>();
								const inCounts = new Map<string, number>();
								for (const n of result.nodes) {
									outCounts.set(n.id, 0);
									inCounts.set(n.id, 0);
								}
								for (const edge of internalEdges) {
									outCounts.set(edge.source, (outCounts.get(edge.source) ?? 0) + 1);
									inCounts.set(edge.target, (inCounts.get(edge.target) ?? 0) + 1);
								}

								const isBranching = [...outCounts.values()].some(c => c > 1) || [...inCounts.values()].some(c => c > 1);
								if (isBranching) {
									toast.info('Pasting branching structures is not supported yet; only linear chains are persisted');
								}

								// Topological order (falls back to y-order if something is off).
								const inDegree = new Map<string, number>();
								for (const [id, count] of inCounts.entries()) {
									inDegree.set(id, count);
								}

								const queue = result.nodes
									.filter(n => (inDegree.get(n.id) ?? 0) === 0)
									.sort((a, b) => a.position.y - b.position.y)
									.map(n => n.id);

								const orderedIds: string[] = [];
								while (queue.length > 0) {
									const currentId = queue.shift()!;
									orderedIds.push(currentId);

									for (const edge of internalEdges) {
										if (edge.source !== currentId) {
											continue;
										}
										const nextId = edge.target;
										inDegree.set(nextId, (inDegree.get(nextId) ?? 0) - 1);
										if ((inDegree.get(nextId) ?? 0) === 0) {
											queue.push(nextId);
										}
									}
								}

								const orderedNodes =
									orderedIds.length === result.nodes.length
										? orderedIds.map(id => nodeById.get(id)!).filter(Boolean)
										: [...result.nodes].sort((a, b) => a.position.y - b.position.y);

								const stepsToPersist = orderedNodes.flatMap(node => {
									const data = node.data;
									if (!isAutomationStepNodeData(data) || !data.automationStep) {
										return [];
									}
									return [{ stepType: data.stepType, step: data.automationStep as Api.IAutomationStep }];
								});

								if (stepsToPersist.length > 0) {
									onPasteSteps(closestTerminus, stepsToPersist).catch(() => {
										toast.error('Failed to paste step(s) on server');
									});
								}
							}
						} else {
							// No terminus available — fallback to disconnected paste at mouse position.
							setNodes(prevNodes => [...prevNodes, ...result.nodes]);
							setEdges(prevEdges => [...prevEdges, ...result.edges]);
							setNodes(prevNodes =>
								prevNodes.map(n => ({
									...n,
									selected: result.newNodeIds.includes(n.id),
								}))
							);
						}
					}
				}
				return;
			}

			// Select all - Ctrl/Cmd+A (selectable mode only)
			if (isModKey && e.key === 'a' && modeConfig.elementsSelectable) {
				e.preventDefault();
				const allNodes = getReactFlowNodes();
				setNodes(allNodes.map(node => ({ ...node, selected: true })));
				return;
			}

			// Select branch - Ctrl/Cmd+B (selectable mode only)
			if (isModKey && e.key === 'b' && modeConfig.elementsSelectable) {
				const selectedNodes = nodes.filter(n => n.selected);
				if (selectedNodes.length === 1) {
					e.preventDefault();
					const branchNodeIds = selectBranchNodes(selectedNodes[0].id);
					setNodes(
						nodes.map(node => ({
							...node,
							selected: branchNodeIds.includes(node.id),
						}))
					);
					toast.info(`Selected ${branchNodeIds.length} node(s) in branch`);
				}
				return;
			}

			// Expand/collapse all - Ctrl/Cmd+E (works in both read-only and editing modes)
			if (isModKey && key === 'e') {
				e.preventDefault();

				// If ANY step node is collapsed, expand all; otherwise collapse all.
				const stepNodes = nodes.filter(n => n.type?.endsWith('Step'));
				const anyCollapsed = stepNodes.some(n => {
					const d = n.data as { isExpanded?: boolean };
					return !d.isExpanded;
				});
				const targetExpanded = anyCollapsed;

				const updatedNodes = nodes.map(n => {
					// Step nodes: set isExpanded and isCompact (inverse).
					if (n.type?.endsWith('Step')) {
						return {
							...n,
							data: { ...n.data, isExpanded: targetExpanded, isCompact: !targetExpanded },
						};
					}
					// Trigger node.
					if (n.type === 'trigger') {
						return {
							...n,
							data: { ...n.data, isExpanded: targetExpanded },
						};
					}
					// Segment node.
					if (n.type === 'segment') {
						return {
							...n,
							data: { ...n.data, isExpanded: targetExpanded },
						};
					}
					return n;
				}) as typeof nodes;

				// Re-layout so nodes don't overlap after height changes.
				const layoutResult = applyLayout(updatedNodes, edges);
				setNodes(layoutResult.nodes);
				setEdges(layoutResult.edges);

				toast.info(targetExpanded ? 'Expanded all nodes' : 'Collapsed all nodes');
				return;
			}

			// Duplicate - Ctrl/Cmd+D (editing mode only)
			if (isModKey && e.key === 'd' && !modeConfig.isReadOnly) {
				const selectedNodes = nodes.filter(n => n.selected);
				if (selectedNodes.length > 0) {
					e.preventDefault();

					if (selectedNodes.some(node => node.type === NODE_TYPES.TRIGGER_AUTOMATION_STEP)) {
						toast.error(TRIGGER_AUTOMATION_LAST_STEP_TOAST);
						return;
					}

					// Single-node duplicate: insert directly after the node in the chain.
					if (selectedNodes.length === 1) {
						const sourceNode = selectedNodes[0];
						copyPaste.copySelection([sourceNode.id]);

						const nodeHeight = sourceNode.measured?.height || 100;
						const pastePositionSingle = { x: sourceNode.position.x, y: sourceNode.position.y + nodeHeight + 100 };
						const pasteResultSingle = copyPaste.pasteAtPosition(pastePositionSingle);
						if (pasteResultSingle && pasteResultSingle.nodes.length === 1) {
							takeSnapshot('paste', `Duplicate ${pasteResultSingle.nodes.length} node(s)`);

							const newNode = pasteResultSingle.nodes[0];
							const nodesWithNew = [...nodes, newNode];
							const edgesWithNew = [...edges, ...pasteResultSingle.edges];
							const moved = moveNodeAfter(nodesWithNew, edgesWithNew, newNode.id, sourceNode.id);

							if (moved.success) {
								setNodes(moved.nodes.map(n => ({ ...n, selected: n.id === newNode.id })));
								setEdges(moved.edges);
								toast.success('Node duplicated');
							}

							// Persist duplication to the API
							if (onDuplicateStep && isAutomationStepNodeData(sourceNode.data)) {
								const stepId = sourceNode.data.stepId;
								const templateId = sourceNode.data.automationTemplate?.id;
								const stepType = sourceNode.data.stepType;
								const step = sourceNode.data.automationStep;
								// Determine the successor stepId in this template so we can insert "directly below".
								const successorNodeId = edges.find(edge => edge.source === sourceNode.id)?.target;
								const successorStepId = successorNodeId
									? (() => {
											const successor = nodes.find(n => n.id === successorNodeId);
											if (!successor || !isAutomationStepNodeData(successor.data)) {
												return undefined;
											}
											return successor.data.stepId;
										})()
									: undefined;

								if (stepId && templateId && step) {
									onDuplicateStep({ templateId, stepType, step, beforeId: successorStepId }).catch(() => {
										toast.error('Failed to duplicate step on server');
									});
								}
							}
						}
						return;
					}

					// Multi-node duplicate: keep previous behavior (duplicate near the selection).
					copyPaste.copySelection(selectedNodes.map(n => n.id));
					const mouseFlowPosition = screenToFlowPosition(mousePositionRef.current);
					const closestTerminus = findClosestTerminus(mouseFlowPosition, chainTermini);
					const lowestY = Math.max(...selectedNodes.map(n => n.position.y + (n.measured?.height || 100)));
					const centerX = selectedNodes.reduce((sum, n) => sum + n.position.x, 0) / selectedNodes.length;
					const pastePositionMulti = { x: centerX, y: lowestY + 100 };

					const pasteResultMulti = copyPaste.pasteAtPosition(pastePositionMulti);
					if (pasteResultMulti) {
						takeSnapshot('paste', `Duplicate ${pasteResultMulti.nodes.length} node(s)`);
						if (closestTerminus) {
							const layoutResult = insertPastedNodesAtTerminus(pasteResultMulti, closestTerminus, nodes, edges);
							setNodes(layoutResult.nodes.map(n => ({ ...n, selected: pasteResultMulti.newNodeIds.includes(n.id) })));
							setEdges(layoutResult.edges);
						} else {
							setNodes(prevNodes => {
								const deselected = prevNodes.map(n => ({ ...n, selected: false }));
								return [...deselected, ...pasteResultMulti.nodes.map(n => ({ ...n, selected: true }))];
							});
							setEdges(prevEdges => [...prevEdges, ...pasteResultMulti.edges]);
						}
						toast.success(`Duplicated ${pasteResultMulti.nodes.length} node(s)`);
					}
				}
				return;
			}

			// Delete - Delete/Backspace (editing mode only)
			if ((e.key === 'Delete' || e.key === 'Backspace') && !modeConfig.isReadOnly) {
				const selectedNodes = nodes.filter(node => node.selected);
				if (selectedNodes.length > 0) {
					e.preventDefault();
					e.stopPropagation();

					// Collect segment-branch deletions up-front so we can persist them separately.
					const selectedIds = selectedNodes.map(n => n.id);
					const { casesToDelete, skipStepDeletionNodeIds } = buildBranchCaseDeletionPlan(selectedIds, nodes, edges);
					const result = deleteNodes(selectedIds, nodes, edges);
					if (result.deletedCount > 0) {
						takeSnapshot('node_delete', `Delete ${result.deletedCount} node(s)`);
						setNodes(result.nodes);
						setEdges(result.edges);

						// Persist segment-branch deletions to the API.
						if (onDeleteBranchCases && casesToDelete.length > 0) {
							onDeleteBranchCases(casesToDelete).catch(() => {
								toast.error('Failed to persist branch deletion to server');
							});
						}

						// Persist step deletions to the API (excluding branch steps removed via case deletion).
						if (onDeleteSteps && result.deletedSteps.length > 0) {
							const stepsToPersist = result.deletedSteps.filter(s => !skipStepDeletionNodeIds.has(s.nodeId || ''));
							if (stepsToPersist.length > 0) {
								onDeleteSteps(stepsToPersist).catch(() => {
									toast.error('Failed to persist step deletion to server');
								});
							}
						}
					}

					if (result.protectedNodeIds.length > 0) {
						toast.info(
							`${result.protectedNodeIds.length} protected node(s) skipped ` +
								'(triggers, default segments, and behavior branches cannot be deleted directly)'
						);
					}
				}
				return;
			}

			// Escape - Cancel group drag
			if (e.key === 'Escape' && groupDragState.isDragging) {
				e.preventDefault();
				cancelGroupDrag();
				return;
			}

			// Open search - Ctrl/Cmd+F or / (when search is available)
			if (openSearch) {
				if ((isModKey && e.key === 'f') || (e.key === '/' && !isModKey)) {
					e.preventDefault();
					openSearch();
					return;
				}
			}

			// Zoom to selection - Z (when nodes are selected)
			if (e.key === 'z' && !isModKey && modeConfig.elementsSelectable) {
				const selectedNodes = nodes.filter(n => n.selected);
				if (selectedNodes.length > 0) {
					e.preventDefault();
					fitView({ nodes: selectedNodes, padding: 0.3, maxZoom: 1.5, duration: 400 });
					return;
				}
			}

			// Fit view - F or Ctrl/Cmd+0 (always available)
			if (e.key === 'f' || (isModKey && e.key === '0')) {
				e.preventDefault();
				fitView(DEFAULT_CANVAS_FIT_VIEW_OPTIONS);
				return;
			}

			// Zoom in - Ctrl/Cmd++
			if (isModKey && e.key === '+') {
				e.preventDefault();
				zoomIn({ duration: 200 });
				return;
			}

			// Zoom out - Ctrl/Cmd+-
			if (isModKey && e.key === '-') {
				e.preventDefault();
				zoomOut({ duration: 200 });
				return;
			}
		};

		window.addEventListener('keydown', handleKeyPress);
		return () => window.removeEventListener('keydown', handleKeyPress);
	}, [
		nodes,
		edges,
		setNodes,
		setEdges,
		fitView,
		zoomIn,
		zoomOut,
		getReactFlowNodes,
		modeConfig.elementsSelectable,
		modeConfig.isReadOnly,
		undoRedo,
		copyPaste,
		takeSnapshot,
		screenToFlowPosition,
		selectBranchNodes,
		groupDragState.isDragging,
		cancelGroupDrag,
		chainTermini,
		openSearch,
		onDeleteSteps,
		onDeleteBranchCases,
		onDuplicateStep,
		onPasteSteps,
		excludedStepTypes,
	]);
}
